import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { V, Haptic } from '../../utils/theme';
import { appName } from '../../utils/brand';
import { LS } from '../../utils/storage';
import { Icons } from '../../components/Icons';
import { Card, Btn, Field, Sheet, Chip, Stat, Progress, Skeleton, SkeletonCard, MsgBannerCtrl, SuccessToastCtrl, ConfirmCtrl } from '../../components/ui';
import { today, ago, fmtShort, fmtFull, uid, friendDisplayName, convW, wUnit, calc1RM, calcStreak } from '../../utils/helpers';
import { SocialAPI, SYNC_URL } from '../../utils/sync';
import { ShareCard } from '../../utils/share';
import { BADGE_DEFS, calcEarnedBadges } from '../../data/badges';
import { useLayout } from '../../utils/responsive';
import { IRON_RANKS } from '../../data/ranks';
import { HelpBtn, compressImage } from '../features';
import { IronScoreCard, RankBadge, DailyMissionsCard } from '../gamification';
import { useStreak, calcReadiness } from '../../components/dialogs';
import { SectionGrid } from '../hubs';
import { MS_PER_MINUTE, MS_PER_HOUR, MS_PER_DAY, ONLINE_THRESHOLD_MS } from '../../utils/constants';
import { apiPost } from '../../utils/api';
import { calcIronScore } from './xpUtils';
import { REACTIONS } from './shared';
import QRCode from 'qrcode';
import { SectionTitle, track } from '../../components/kit';

export function SocialProfile({s,d}){
  const { isDesktop } = useLayout();
  const email=s.profile?.email;const defaultUn=email?.split("@")[0]||"athlete";
  // Empty until the server confirms a claim. It used to default to the email's local
  // part, so the screen showed a handle you did not own and put it in your share link.
  const [username,setUsername]=useState(LS.get("ft-username-claimed")?(LS.get("ft-username")||""):"");
  const [editingUn,setEditingUn]=useState(false);
  const [newUn,setNewUn]=useState(username||defaultUn);  // suggestion only, not a claim
  const [unError,setUnError]=useState("");
  const streak=useStreak(s.workouts);
  const getBestLift=(id)=>{let b=0;(s.workouts||[]).forEach(w=>w.exercises.forEach(e=>{if(e.exerciseId===id)e.sets.forEach(st=>{if(st.weight>b)b=st.weight;});}));return b;};
  const big3=getBestLift("bench")+getBestLift("squat")+getBestLift("deadlift");
  // Privacy starts from the last known local copy so the UI paints immediately, but the
  // server is the authority and overwrites it as soon as the friends read lands (below).
  // It used to start from these defaults and stay there: a browser that had never seen
  // the server's settings would then send the whole object on the next toggle, silently
  // turning discovery back on for someone who had switched it off on their phone.
  const [privacy,setPrivacy]=useState(LS.get("ft-privacy")||{workouts:false,macros:false,photos:false,body:false});
  const [privacyLoaded,setPrivacyLoaded]=useState(false);
  // Sharing toggles default OFF; presence/discovery toggles default ON (absent key = on)
  const DEFAULT_ON=new Set(["online","readReceipts","discoverable"]);
  const isOn=(k)=>DEFAULT_ON.has(k)?privacy[k]!==false:!!privacy[k];
  const [blocked,setBlocked]=useState([]);
  const unblock=async(target)=>{await SocialAPI.unblockUser(email,target);setBlocked(b=>b.filter(x=>x.email!==target));SuccessToastCtrl.show("Unblocked");};
  // Privacy lives in localStorage for the UI, but the server gates friend_workouts / feed
  // visibility on users.privacy_settings — so every toggle is pushed immediately.
  const toggleP=async(k)=>{
    const next=!isOn(k);
    const prev=privacy;
    const n={...privacy,[k]:next};setPrivacy(n);LS.set("ft-privacy",n);
    if(!email)return;
    // Send ONLY the key that changed — the server merges it. Sending the whole object
    // is what let a stale local copy overwrite settings made on another device.
    const r=await SocialAPI.updateProfile(email,{privacy:{[k]:next}});
    // There is no retry queue, so "will sync when online" was never true. A setting the
    // server did not accept is not saved, and the switch goes back to what is real.
    if(!r?.success){setPrivacy(prev);LS.set("ft-privacy",prev);
      SuccessToastCtrl.error("Couldn't save that — check your connection and try again");}
  };
  const [bio,setBio]=useState(s.profile?.bio||LS.get("ft-bio")||"");
  const [editBio,setEditBio]=useState(false);
  const profilePic=LS.get("ft-profile-pic");
  const bannerPic=LS.get("ft-profile-banner");
  const picRef=useRef(null);
  const bannerRef=useRef(null);

  const syncProfile=(extra)=>{
    if(!email)return;
    SocialAPI.updateProfile(email,{
      avatar:LS.get("ft-profile-pic"),
      banner:LS.get("ft-profile-banner"),
      bio:LS.get("ft-bio"),
      username:LS.get("ft-username-claimed")?LS.get("ft-username"):undefined,
      ...extra
    });
  };

  const handleProfilePic=async(e)=>{
    const file=e.target.files?.[0];if(!file)return;
    const compressed=await compressImage(file,200,0.7);
    if(compressed){LS.set("ft-profile-pic",compressed);SuccessToastCtrl.show("Profile photo updated");
      d({type:"SET_PROFILE",profile:{...s.profile,avatar:compressed}});
      syncProfile({avatar:compressed});}
    e.target.value="";
  };

  const handleBanner=async(e)=>{
    const file=e.target.files?.[0];if(!file)return;
    // Banner: wide crop, lower quality — 900×300 effective canvas
    const banner=await (()=>new Promise(resolve=>{
      const img=new Image();
      img.onload=()=>{
        URL.revokeObjectURL(img.src);
        const targetW=900,targetH=300;
        const srcRatio=img.width/img.height,tgtRatio=targetW/targetH;
        let sx=0,sy=0,sw=img.width,sh=img.height;
        if(srcRatio>tgtRatio){sw=img.height*tgtRatio;sx=(img.width-sw)/2;}
        else{sh=img.width/tgtRatio;sy=(img.height-sh)/2;}
        const c=document.createElement("canvas");c.width=targetW;c.height=targetH;
        c.getContext("2d").drawImage(img,sx,sy,sw,sh,0,0,targetW,targetH);
        resolve(c.toDataURL("image/jpeg",0.65));
      };
      img.onerror=()=>{URL.revokeObjectURL(img.src);resolve(null);};
      img.src=URL.createObjectURL(file);
    }))();
    if(banner){LS.set("ft-profile-banner",banner);SuccessToastCtrl.show("Banner updated");
      syncProfile({banner});}
    e.target.value="";
  };

  const saveBio=()=>{
    const trimmed=bio.trim().slice(0,120);
    LS.set("ft-bio",trimmed);
    d({type:"SET_PROFILE",profile:{...s.profile,bio:trimmed}});
    setEditBio(false);SuccessToastCtrl.show("Bio saved");
    syncProfile({bio:trimmed});
  };

  // Friend code: the one the server issued at signup (users.friend_code). It's what
  // add-by-code looks up, so a locally derived code would never match. Fetch it once
  // if the profile doesn't carry it yet (accounts created before it was stored).
  const friendCode=s.profile?.friendCode||null;
  useEffect(()=>{
    if(!email)return;
    let alive=true;
    SocialAPI.getFriends(email).then(r=>{
      if(!alive||!r)return;
      setBlocked(r.blocked||[]);
      const code=r.me?.friendCode;
      if(!friendCode&&code)d({type:"SET_PROFILE",profile:{...s.profile,friendCode:code}});
      // The server already sends the real settings with this response; they were simply
      // never read. Adopt them once per mount so the switches show what is actually in
      // force rather than this browser's defaults.
      if(r.me?.privacy&&typeof r.me.privacy==="object"){
        setPrivacy(r.me.privacy);LS.set("ft-privacy",r.me.privacy);
      }
      setPrivacyLoaded(true);
    });
    return()=>{alive=false;};
  },[email,friendCode]);
  // QR is generated on-device (no third-party image service, works offline)
  const addUrl=`https://ironlog.space/add?code=${friendCode||""}${username?`&u=${username}`:""}`;
  const [qrSvg,setQrSvg]=useState("");
  useEffect(()=>{
    if(!friendCode){setQrSvg("");return;}
    let alive=true;
    QRCode.toString(addUrl,{type:"svg",margin:0,errorCorrectionLevel:"M",color:{dark:"#0e0e16",light:"#ffffff"}}).then(svg=>{if(alive)setQrSvg(svg);}).catch(()=>{});
    return()=>{alive=false;};
  },[addUrl,friendCode]);

  // QR Code SVG generator (simple version matrix)

  // The app used to silently claim `email.split("@")[0]` as your public handle on first
  // load — a permanent, public identifier assigned without asking — and retried on every
  // mount when it was taken. iOS has always required an explicit claim. Adopt the handle
  // the server already holds; claiming a new one is the user's decision.
  useEffect(()=>{
    if(!email)return;
    let alive=true;
    SocialAPI.getFriends(email).then(r=>{
      if(!alive)return;
      const un=r?.me?.username;
      if(un){setUsername(un);LS.set("ft-username",un);LS.set("ft-username-claimed",true);}
    });
    return()=>{alive=false;};
  },[email]);

  const claimUsername=async()=>{
    const clean=newUn.toLowerCase().replace(/[^a-z0-9_]/g,"");
    if(clean.length<3){setUnError("Min 3 characters");return;}
    const r=await SocialAPI.setUsername(email,clean);
    if(r?.success){setUsername(r.username);LS.set("ft-username",r.username);LS.set("ft-username-claimed",true);
      setEditingUn(false);setUnError("");SuccessToastCtrl.show(`Username set to @${r.username}`);
      syncProfile({username:r.username});}
    else setUnError(r?.error||"Failed");
  };

  return(
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      {/* Profile card with banner */}
      <Card style={{padding:0,overflow:"hidden",textAlign:"center"}}>
        <input ref={picRef} type="file" accept={"image/" + "*"} onChange={handleProfilePic} style={{display:"none"}}/>
        <input ref={bannerRef} type="file" accept={"image/" + "*"} onChange={handleBanner} style={{display:"none"}}/>

        {/* Banner area */}
        <div onClick={()=>bannerRef.current?.click()} style={{width:"100%",height:110,position:"relative",cursor:"pointer",
          background:bannerPic?"none":`linear-gradient(135deg,${V.accent}40,${V.purple}40,#ec489940)`,overflow:"hidden"}}>
          {bannerPic&&<img src={bannerPic} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>}
          <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.18)",display:"flex",alignItems:"center",
            justifyContent:"center",opacity:0,transition:"opacity .2s"}}
            onMouseEnter={e=>e.currentTarget.style.opacity=1} onMouseLeave={e=>e.currentTarget.style.opacity=0}>
            <span style={{fontSize:11,color:"#fff",fontWeight:700,background:"rgba(0,0,0,0.5)",padding:"4px 12px",borderRadius:20}}>Change Banner</span>
          </div>
          {/* Always-visible edit hint for mobile */}
          <div style={{position:"absolute",bottom:6,right:8,background:"rgba(0,0,0,0.5)",borderRadius:6,padding:"3px 8px",
            display:"flex",alignItems:"center",gap:4}}>
            <span style={{fontSize:11,color:"#fff"}}>📷</span>
            <span style={{fontSize:11,color:"rgba(255,255,255,0.85)"}}>Edit banner</span>
          </div>
        </div>

        {/* Avatar — overlaps banner */}
        <div style={{padding:"0 16px 16px",marginTop:-36}}>
          <div style={{position:"relative",display:"inline-block",marginBottom:8}}>
            <div onClick={()=>picRef.current?.click()} style={{width:72,height:72,borderRadius:20,
              background:profilePic?"none":`linear-gradient(135deg,${V.accent},#ec4899)`,
              display:"inline-flex",alignItems:"center",justifyContent:"center",cursor:"pointer",
              overflow:"hidden",border:`3px solid ${V.bg}`,position:"relative",boxShadow:"0 4px 12px rgba(0,0,0,0.4)"}}>
              {profilePic?<img src={profilePic} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                :<span style={{fontSize:28,color:V.bg,fontWeight:900}}>{(s.profile?.firstName||username||defaultUn)[0]?.toUpperCase()}</span>}
            </div>
            <div style={{position:"absolute",bottom:0,right:0,width:22,height:22,borderRadius:11,
              background:V.accent,display:"flex",alignItems:"center",justifyContent:"center",border:`2px solid ${V.bg}`,
              pointerEvents:"none"}}>
              <span style={{fontSize:11,color:V.bg}}>📷</span>
            </div>
          </div>
        <div style={{fontSize:18,fontWeight:800,color:V.text}}>{s.profile?.firstName||username||defaultUn}</div>
        {/* Rank badge */}
        {(()=>{const {rank}=calcIronScore(s);return <div style={{marginBottom:4}}><RankBadge rank={rank} size="sm"/></div>;})()}
        {!editingUn?(
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:4}}>
            <span style={{fontSize:11,color:username?V.text3:V.text3,fontStyle:username?"normal":"italic"}}>{username?`@${username}`:"no username yet"}</span>
            <button onClick={()=>{setEditingUn(true);setNewUn(username||defaultUn);}} style={{background:"none",border:"none",cursor:"pointer",padding:2}}>
              <span style={{fontSize:11,color:V.accent}}>edit</span>
            </button>
          </div>
        ):(
          <div style={{display:"flex",gap:6,justifyContent:"center",marginTop:4}}>
            <input value={newUn} onChange={e=>setNewUn(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g,""))}
              maxLength={20} style={{padding:"6px 10px",background:V.inputBg,border:`1px solid ${V.cardBorder}`,
                borderRadius:6,color:V.text,fontSize:12,outline:"none",fontFamily:V.mono,width:140,textAlign:"center"}}/>
            <button onClick={claimUsername} style={{padding:"6px 10px",borderRadius:6,background:V.accent,border:"none",
              cursor:"pointer",fontSize:11,fontWeight:700,color:V.bg,fontFamily:V.font}}>Save</button>
            <button onClick={()=>{setEditingUn(false);setUnError("");}} style={{padding:"6px 8px",borderRadius:6,
              background:V.inputBg,border:`1px solid ${V.cardBorder}`,cursor:"pointer",fontSize:11,color:V.text3}}>✕</button>
          </div>
        )}
        {unError&&<div style={{fontSize:11,color:V.danger,marginTop:4}}>{unError}</div>}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6,margin:"12px 0"}}>
          {[{l:"STREAK",v:streak,c:V.accent},{l:"WORKOUTS",v:(s.workouts||[]).length,c:V.purple},{l:"BIG 3",v:big3||"—",c:V.warn}].map(st=>(
            <div key={st.l} style={{padding:6,borderRadius:6,background:V.card}}>
              <div style={{fontSize:16,fontWeight:800,color:st.c,fontFamily:V.mono}}>{st.v}</div>
              <div style={{fontSize:11,color:V.text3}}>{st.l}</div>
            </div>
          ))}
        </div>
        {big3>0&&<div style={{fontSize:11,color:V.text3}}>B: {getBestLift("bench")} · S: {getBestLift("squat")} · D: {getBestLift("deadlift")} {wUnit(s.units)}</div>}
        {/* Bio */}
        <div style={{marginTop:10,borderTop:`1px solid ${V.cardBorder}`,paddingTop:10}}>
          {!editBio?(
            <div>
              {bio?<div style={{fontSize:12,color:V.text2,lineHeight:1.5,fontStyle:"italic"}}>{bio}</div>
                :<div style={{fontSize:11,color:V.text3}}>No bio yet</div>}
              <button onClick={()=>setEditBio(true)} style={{background:"none",border:"none",cursor:"pointer",
                fontSize:11,color:V.accent,marginTop:4}}>{bio?"Edit bio":"Add bio"}</button>
            </div>
          ):(
            <div>
              <textarea value={bio} onChange={e=>{if(e.target.value.length<=120)setBio(e.target.value);}} placeholder="Tell people about yourself..."
                maxLength={120} rows={2} style={{width:"100%",padding:"8px 10px",background:V.inputBg,
                  border:`1px solid ${V.cardBorder}`,borderRadius:8,color:V.text,fontSize:12,outline:"none",
                  fontFamily:V.font,resize:"none",boxSizing:"border-box"}}/>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:4}}>
                <span style={{fontSize:11,color:bio.length>100?V.warn:V.text3}}>{bio.length}/120</span>
                <div style={{display:"flex",gap:4}}>
                  <button onClick={()=>{setEditBio(false);setBio(s.profile?.bio||LS.get("ft-bio")||"");}} style={{padding:"4px 8px",borderRadius:4,
                    background:V.inputBg,border:`1px solid ${V.cardBorder}`,cursor:"pointer",fontSize:11,color:V.text3}}>Cancel</button>
                  <button onClick={saveBio} style={{padding:"4px 10px",borderRadius:4,background:V.accent,border:"none",
                    cursor:"pointer",fontSize:11,fontWeight:700,color:V.bg}}>Save</button>
                </div>
              </div>
            </div>
          )}
        </div>
        </div>{/* end padding wrapper */}
      </Card>

      {/* Badges on Profile */}
      {(()=>{
        const {checks,dates}=calcEarnedBadges(s);
        const earned=BADGE_DEFS.filter(b=>checks[b.id]);
        if(earned.length===0)return null;
        const fmtDate=(d)=>d?new Date(d+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}):"";
        return(
          <Card style={{padding:14}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
              <div style={{fontSize:13,fontWeight:800,color:V.text}}>🏅 My Badges</div>
              <button onClick={()=>d({type:"TAB",tab:"social_badges"})} style={{background:"none",border:"none",
                cursor:"pointer",fontSize:11,color:V.accent,fontWeight:700}}>View All →</button>
            </div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {earned.map(b=>(
                <div key={b.id} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:3,minWidth:56,maxWidth:70}}>
                  <div style={{width:46,height:46,borderRadius:12,
                    background:`linear-gradient(135deg,${V.accent}18,${V.purple}18)`,
                    border:`1.5px solid ${V.accent}35`,
                    display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>
                    {b.icon}
                  </div>
                  <span style={{fontSize:11,color:V.accent,fontWeight:700,textAlign:"center",lineHeight:1.2}}>{b.name}</span>
                  {dates[b.id]&&<span style={{fontSize:11,color:V.text3,textAlign:"center"}}>{fmtDate(dates[b.id])}</span>}
                </div>
              ))}
            </div>
          </Card>
        );
      })()}

      {/* Friend Code */}
      <Card style={{padding:16,textAlign:"center"}}>
        <div style={{fontSize:12,fontWeight:700,color:V.text,marginBottom:4}}>Your Friend Code</div>
        <div style={{fontSize:28,fontWeight:900,color:friendCode?V.accent:V.text3,fontFamily:V.mono,letterSpacing:4,margin:"8px 0"}}>{friendCode||"······"}</div>
        <div style={{fontSize:11,color:V.text3,marginBottom:10}}>{friendCode?"Share this code — friends enter it to add you":"Fetching your code…"}</div>
        <div style={{display:"flex",gap:8}}>
          <Btn full disabled={!friendCode} onClick={()=>{navigator.clipboard?.writeText(friendCode);SuccessToastCtrl.show("Code copied");}}>Copy Code</Btn>
          <Btn v="secondary" full onClick={()=>{
            const addUrl=`https://ironlog.space/add?code=${friendCode}${username?`&u=${username}`:''}`;
            const shareText=`Add me on ${appName()}! Tap the link or enter my code: ${friendCode}`;
            if(navigator.share)navigator.share({title:`Add me on ${appName()}`,text:shareText,url:addUrl}).catch(()=>{});
            else{navigator.clipboard?.writeText(`${shareText}\n${addUrl}`);SuccessToastCtrl.show("Copied to clipboard");}
          }}>Share</Btn>
        </div>
      </Card>

      {/* QR Code — real scannable */}
      <Card style={{padding:16,textAlign:"center"}}>
        <div style={{fontSize:12,fontWeight:700,color:V.text,marginBottom:8}}>QR Code</div>
        <div style={{display:"inline-block",padding:12,background:"#fff",borderRadius:12,marginBottom:8}}>
          {/* QR encodes a real URL so any scanner opens ironlog.space/add?code=IRON-XXXX */}
          {/* When opened inside the app the deep-link handler auto-fills the add-friend sheet */}
          {qrSvg
            ?<div role="img" aria-label={`QR code for ${addUrl}`} style={{width:140,height:140}} dangerouslySetInnerHTML={{__html:qrSvg}}/>
            :<div style={{width:140,height:140,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:"#666"}}>{friendCode?"Generating…":"Waiting for code"}</div>}
        </div>
        <div style={{fontSize:11,color:V.text3,marginBottom:4}}>Scan to add — works from any camera app</div>
        <div style={{fontSize:11,color:V.text3,fontFamily:V.mono,opacity:0.5,wordBreak:"break-all",maxWidth:160,margin:"0 auto"}}>
          ironlog.space/add?code={friendCode}
        </div>
      </Card>

      {/* Privacy controls */}
      <Card style={{padding:14}}>
        <SectionTitle icon={Icons.target} color={V.purple} style={{marginBottom:8}}>Privacy</SectionTitle>
        <div style={{fontSize:11,color:V.text3,marginBottom:8}}>Private by default. Toggle what friends see.</div>
        {[{k:"workouts",l:"Workouts & PRs"},{k:"macros",l:"Macros"},{k:"photos",l:"Photos"},{k:"body",l:"Body metrics"},
          // The server has always honoured `challenges` — it gates the streak, big 3 and
          // IronScore on leaderboards and on the public profile card — and iOS has always
          // offered the switch. The web app simply never listed it, so a browser-only user
          // could not opt out, or even discover that opting out was possible.
          {k:"challenges",l:"Challenge progress",sub:"Streak, big 3 and IronScore on leaderboards"},
          {k:"online",l:"Show online status",sub:"Friends see when you were last active"},
          {k:"readReceipts",l:"Send read receipts",sub:"Friends see when you've read their messages"},
          {k:"discoverable",l:"Discoverable",sub:"Appear in search and friend suggestions"}].map(p=>(
          <div key={p.k} role="switch" aria-checked={isOn(p.k)} tabIndex={0} onClick={()=>toggleP(p.k)} onKeyDown={e=>{if(e.key==="Enter"||e.key===" "){e.preventDefault();toggleP(p.k);}}}
            style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,padding:"8px 0",borderBottom:`1px solid ${V.divider}`,cursor:"pointer"}}>
            <div><div style={{fontSize:12,color:V.text}}>{p.l}</div>{p.sub&&<div style={{fontSize:11,color:V.text3}}>{p.sub}</div>}</div>
            <div style={{width:36,height:20,borderRadius:10,background:isOn(p.k)?V.accent:track(),padding:2,transition:"all .2s",flexShrink:0}}>
              <div style={{width:16,height:16,borderRadius:8,background:"#fff",transform:isOn(p.k)?"translateX(16px)":"translateX(0)",transition:"transform .2s"}}/>
            </div>
          </div>
        ))}
        {blocked.length>0&&(
          <div style={{marginTop:10}}>
            <div style={{fontSize:11,fontWeight:700,color:V.text3,letterSpacing:".04em",marginBottom:4}}>BLOCKED</div>
            {blocked.map(b=>(
              <div key={b.email} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0"}}>
                <span style={{fontSize:12,color:V.text2}}>{b.name}</span>
                <button type="button" onClick={()=>unblock(b.email)} style={{padding:"4px 10px",borderRadius:6,background:V.inputBg,border:`1px solid ${V.cardBorder}`,cursor:"pointer",fontSize:11,color:V.text2,fontFamily:V.font}}>Unblock</button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Social Sub-Page: Badges ───
export function SocialBadges({s}){
  const { isDesktop } = useLayout();
  const {checks,dates}=useMemo(()=>calcEarnedBadges(s),[s.workouts,s.nutrition,s.photos,s.checkins,s.body]);
  const [catFilter,setCatFilter]=useState("All");
  const cats=["All",...new Set(BADGE_DEFS.map(b=>b.cat))];
  const earnedCount=BADGE_DEFS.filter(b=>checks[b.id]).length;
  const filtered=catFilter==="All"?BADGE_DEFS:BADGE_DEFS.filter(b=>b.cat===catFilter);
  const fmtDate=(d)=>d?new Date(d+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}):"";

  return(
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:16,fontWeight:800,color:V.text}}>Badges</div>
          <div style={{fontSize:11,color:V.text3}}>{earnedCount} of {BADGE_DEFS.length} earned</div>
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{fontSize:22,fontWeight:900,color:V.accent,fontFamily:V.mono}}>{earnedCount}</div>
          <div style={{fontSize:11,color:V.text3}}>TOTAL</div>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{height:6,borderRadius:3,background:V.hoverBg}}>
        <div style={{height:"100%",borderRadius:3,background:`linear-gradient(90deg,${V.accent},${V.purple})`,
          width:`${Math.round(earnedCount/BADGE_DEFS.length*100)}%`,transition:"width .5s"}}/>
      </div>

      {/* Earned badges showcase (top 5) */}
      {earnedCount>0&&(
        <Card style={{padding:12}}>
          <div style={{fontSize:11,fontWeight:700,color:V.text3,marginBottom:8}}>RECENTLY EARNED</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {BADGE_DEFS.filter(b=>checks[b.id]).slice(0,6).map(b=>(
              <div key={b.id} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2,minWidth:52}}>
                <div style={{width:44,height:44,borderRadius:12,background:`linear-gradient(135deg,${V.accent}18,${V.purple}18)`,
                  border:`1.5px solid ${V.accent}40`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>
                  {b.icon}
                </div>
                <span style={{fontSize:11,color:V.accent,fontWeight:700,textAlign:"center",maxWidth:52,lineHeight:1.2}}>{b.name}</span>
                {dates[b.id]&&<span style={{fontSize:11,color:V.text3}}>{fmtDate(dates[b.id])}</span>}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Category filter */}
      <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
        {cats.map(c=><Chip key={c} label={c} active={catFilter===c} onClick={()=>setCatFilter(c)}/>)}
      </div>

      {/* Badge grid */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
        {filtered.map(b=>{
          const earned=checks[b.id];
          const earnDate=dates[b.id];
          return(
            <Card key={b.id} style={{padding:12,opacity:earned?1:0.38,
              border:earned?`1px solid ${V.accent}30`:`1px solid ${V.cardBorder}`,
              background:earned?`linear-gradient(135deg,${V.accent}06,${V.purple}06)`:undefined}}>
              <div style={{fontSize:26,marginBottom:4}}>{b.icon}</div>
              <div style={{fontSize:12,fontWeight:700,color:earned?V.text:V.text3,lineHeight:1.2,marginBottom:2}}>{b.name}</div>
              <div style={{fontSize:11,color:V.text3,lineHeight:1.3,marginBottom:6}}>{b.desc}</div>
              {earned?(
                <div style={{display:"flex",alignItems:"center",gap:4}}>
                  <span style={{fontSize:11,color:V.accent,fontWeight:700}}>✅ Earned</span>
                  {earnDate&&<span style={{fontSize:11,color:V.text3}}>{fmtDate(earnDate)}</span>}
                </div>
              ):(
                <div style={{fontSize:11,color:V.text3,fontStyle:"italic"}}>Locked</div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ─── Social Sub-Page: Notifications ───
export function SocialNotifications({s,d}){
  const { isDesktop } = useLayout();
  const email=s.profile?.email;
  const [notifs,setNotifs]=useState(null);
  const [filter,setFilter]=useState("unread");
  const [loading,setLoading]=useState(true);

  const load=()=>{
    if(!email)return;
    setLoading(true);
    SocialAPI.getNotifications(email).then(r=>{
      if(r)setNotifs(r);
      setLoading(false);
    });
  };
  useEffect(load,[email]);

  const markAllRead=async()=>{
    await SocialAPI.markRead(email,"all");
    setNotifs(n=>({...n,unread:0,notifications:(n?.notifications||[]).map(x=>({...x,read:true}))}));
    SuccessToastCtrl.show("All marked as read");
  };

  const clearRead=async()=>{
    await SocialAPI.clearReadNotifs(email);
    setNotifs(n=>({...n,notifications:(n?.notifications||[]).filter(x=>!x.read)}));
    SuccessToastCtrl.show("Cleared read notifications");
  };

  const deleteOne=async(id)=>{
    await SocialAPI.deleteNotif(email,id);
    setNotifs(n=>({...n,notifications:(n?.notifications||[]).filter(x=>x.id!==id),
      unread:Math.max(0,(n?.unread||0)-(n?.notifications?.find(x=>x.id===id&&!x.read)?1:0))}));
  };

  const markOneRead=async(notif)=>{
    if(!notif.read){
      await SocialAPI.markRead(email,notif.id);
      setNotifs(n=>({...n,unread:Math.max(0,(n?.unread||0)-1),
        notifications:(n?.notifications||[]).map(x=>x.id===notif.id?{...x,read:true}:x)}));
    }
  };

  const tapNotif=(notif)=>{
    markOneRead(notif);
    const meta=notif.metadata||{};
    if(notif.type==="org_dm"||notif.type==="org_announcement"){
      // White-label: coach message / gym announcement → gym inbox (separate from social DMs)
      d({type:"TAB",tab:"org_inbox"});
    }else if(notif.type==="dm"){
      // Open the conversation itself, not the friends list
      if(meta.from)LS.set("ft-open-dm-from",meta.from);
      d({type:"TAB",tab:"social_messages"});
    }else if(notif.type==="group_chat"||meta.group){
      d({type:"TAB",tab:"social_groups"});
    }else if(notif.type==="friend_request"){
      d({type:"TAB",tab:"social_friends"});
    }else if(["pr","milestone","comment","reaction"].includes(notif.type)||meta.event_id){
      d({type:"TAB",tab:"social_feed"});
    }else if(["duel_result","duel_accepted","duel_invite"].includes(notif.type)){
      d({type:"TAB",tab:"social_duels"});
    }
  };

  // One tap from the notification: react to the friend's PR / streak without leaving the list
  const quickReact=async(notif,key)=>{
    const eventId=notif.metadata?.event_id;if(!eventId)return;
    Haptic.light();
    const r=await SocialAPI.react(email,eventId,key);
    if(r){
      LS.set("ft-reacted-today",today());
      markOneRead(notif);
      SuccessToastCtrl.show(`${REACTIONS.find(x=>x.key===key)?.emoji||""} Sent to ${notif.metadata?.name||"your friend"}`);
    } else SuccessToastCtrl.error("Could not react — try again");
  };

  // Clean email from notification body if present (old notifications before fix)
  const cleanBody=(body)=>{
    if(!body)return"";
    return body.replace(/[\w._%+-]+@[\w.-]+\.[a-zA-Z]{2,}/g,m=>{
      const prefix=m.split("@")[0].replace(/[._+-]/g," ");
      return prefix.replace(/\b\w/g,c=>c.toUpperCase()).trim()||"Someone";
    });
  };

  const all=notifs?.notifications||[];
  const filtered=filter==="unread"?all.filter(n=>!n.read):
    filter==="messages"?all.filter(n=>n.type==="dm"||n.type==="group_chat"):all;
  const unreadCount=notifs?.unread||0;
  const readCount=all.filter(n=>n.read).length;

  const timeAgo=(ts)=>{
    if(!ts)return"";
    const m=Math.round((Date.now()-new Date(ts))/60000);
    if(m<1)return"now";if(m<60)return`${m}m`;if(m<1440)return`${Math.round(m/60)}h`;return`${Math.round(m/1440)}d`;
  };

  const typeMeta=(n)=>{
    if(n.type==="dm")return{icon:Icons.activity,color:V.accent};
    if(n.type==="org_dm")return{icon:Icons.activity,color:V.accent2};
    if(n.type==="org_announcement")return{icon:Icons.bell,color:V.warn};
    if(n.type==="group_chat")return{icon:Icons.users,color:V.purple};
    if(n.type==="friend_request")return{icon:Icons.users,color:V.accent2};
    if(n.type==="duel_invite")return{icon:Icons.zap,color:V.danger};
    if(n.type==="pr")return{icon:Icons.trophy,color:V.warn};
    if(n.type==="milestone")return{icon:Icons.flame,color:"#f97316"};
    if(n.type==="comment")return{icon:Icons.activity,color:V.accent2};
    if(n.type==="reaction")return{icon:Icons.flame,color:V.warn};
    if(n.type==="duel_result"||n.type==="duel_accepted")return{icon:Icons.trophy,color:n.metadata?.outcome==="lost"?V.text3:V.accent};
    return{icon:Icons.bell,color:V.text3};
  };

  const acceptDuelInvite=async(notif)=>{
    const meta=notif.metadata||{};
    if(meta.server&&meta.duel_id){
      // Ranked duel: the server tracks it and declares the winner
      const r=await SocialAPI.duelAction(email,"respond",{duel_id:meta.duel_id,accept:true});
      markOneRead(notif);
      SuccessToastCtrl.show(r?.success?"⚔️ Duel accepted — the server keeps score":"Could not accept duel",{tone:r?.success?"success":"error"});
      return;
    }
    const duels=LS.get("ft-duels")||[];
    duels.push({id:meta.duelId||uid(),friendEmail:meta.from,friendName:meta.name||"Challenger",
      metric:meta.metric||"streak",days:meta.days||7,startDate:today(),endDate:ago(-(meta.days||7)),
      status:"active",resultSeen:false,myStartVal:0,theirVal:0});
    LS.set("ft-duels",duels);
    SocialAPI.logEvent(email,"DuelAccepted",{to:meta.from,metric:meta.metric},"private");
    markOneRead(notif);
    SuccessToastCtrl.show("⚔️ Duel accepted! Let's go!");
  };

  const declineDuelInvite=(notif)=>{
    const meta=notif.metadata||{};
    if(meta.server&&meta.duel_id){SocialAPI.duelAction(email,"respond",{duel_id:meta.duel_id,accept:false});deleteOne(notif.id);SuccessToastCtrl.show("Duel declined");return;}
    SocialAPI.logEvent(email,"DuelDeclined",{to:meta.from,metric:meta.metric},"private");
    deleteOne(notif.id);
    SuccessToastCtrl.show("Duel declined");
  };

  return(
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:16,fontWeight:800,color:V.text}}>Notifications</div>
          {unreadCount>0&&<div style={{fontSize:11,color:V.accent}}>{unreadCount} unread</div>}
        </div>
        <div style={{display:"flex",gap:4}}>
          {unreadCount>0&&<button onClick={markAllRead} style={{padding:"4px 10px",borderRadius:6,
            background:`${V.accent}10`,border:`1px solid ${V.accent}20`,cursor:"pointer",fontSize:11,fontWeight:700,color:V.accent,fontFamily:V.font}}>Read All</button>}
          {readCount>0&&<button onClick={clearRead} style={{padding:"4px 10px",borderRadius:6,
            background:V.inputBg,border:`1px solid ${V.cardBorder}`,cursor:"pointer",fontSize:11,color:V.text3,fontFamily:V.font}}>Clear Read</button>}
        </div>
      </div>

      <div style={{display:"flex",gap:4}}>
        {[{id:"unread",l:`Unread${unreadCount>0?` (${unreadCount})`:""}`},{id:"messages",l:"Messages"},{id:"all",l:"All"}].map(f=>(
          <Chip key={f.id} label={f.l} active={filter===f.id} onClick={()=>setFilter(f.id)}/>
        ))}
      </div>

      {loading&&<div><SkeletonCard lines={2}/><div style={{height:8}}/><SkeletonCard lines={2}/></div>}

      {!loading&&filtered.length===0&&(
        <Card style={{padding:30,textAlign:"center"}}>
          <div style={{fontSize:28,marginBottom:8}}>{filter==="unread"?"✅":"🔔"}</div>
          <div style={{fontSize:13,fontWeight:600,color:filter==="unread"?V.accent:V.text3}}>
            {filter==="unread"?"All caught up!":"No notifications yet"}
          </div>
        </Card>
      )}

      {filtered.map(n=>(
        <Card key={n.id} onClick={()=>tapNotif(n)} style={{padding:12,cursor:"pointer",
          borderLeft:n.read?"none":`3px solid ${typeMeta(n).color}`}}>
          <div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
            <div style={{width:32,height:32,borderRadius:10,
              background:n.read?V.inputBg:`${typeMeta(n).color}18`,border:`1px solid ${typeMeta(n).color}${n.read?"20":"40"}`,
              display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              {typeMeta(n).icon({size:15,color:n.read?V.text3:typeMeta(n).color})}
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:12,fontWeight:n.read?500:700,color:n.read?V.text3:V.text}}>{n.title}</span>
                <span style={{fontSize:11,color:V.text3,flexShrink:0}}>{timeAgo(n.created_at)}</span>
              </div>
              <div style={{fontSize:11,color:n.read?V.text3:V.text2,marginTop:2,
                overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{cleanBody(n.body)}</div>
              {(n.type==="pr"||n.type==="milestone")&&n.metadata?.event_id&&!n.read&&(
                <div style={{display:"flex",gap:6,marginTop:6,alignItems:"center"}} onClick={e=>e.stopPropagation()}>
                  <span style={{fontSize:11,color:V.text3}}>React:</span>
                  {REACTIONS.slice(0,4).map(r=>(
                    <button key={r.key} type="button" onClick={()=>quickReact(n,r.key)} aria-label={`React ${r.label}`}
                      style={{padding:"4px 9px",borderRadius:8,background:V.inputBg,border:`1px solid ${V.cardBorder}`,cursor:"pointer",fontSize:14,lineHeight:1}}>{r.emoji}</button>
                  ))}
                </div>
              )}
              {n.type==="duel_invite"&&!n.read&&(
                <div style={{display:"flex",gap:6,marginTop:6}} onClick={e=>e.stopPropagation()}>
                  <button onClick={()=>acceptDuelInvite(n)} style={{padding:"5px 14px",borderRadius:6,background:V.accent,
                    border:"none",cursor:"pointer",fontSize:11,fontWeight:700,color:V.bg,fontFamily:V.font}}>Accept</button>
                  <button onClick={()=>declineDuelInvite(n)} style={{padding:"5px 14px",borderRadius:6,
                    background:V.inputBg,border:`1px solid ${V.cardBorder}`,
                    cursor:"pointer",fontSize:11,color:V.text3,fontFamily:V.font}}>Decline</button>
                </div>
              )}
            </div>
            <button onClick={e=>{e.stopPropagation();deleteOne(n.id);}} style={{
              background:"none",border:"none",cursor:"pointer",padding:4,flexShrink:0,opacity:0.4}}>
              {Icons.x({size:12,color:V.text3})}
            </button>
          </div>
        </Card>
      ))}
    </div>
  );
}

// ─── Social Sub-Page: Compare (You vs Best) ───
